/**
 * FreeRevealStep — shows the free hero past-life teaser.
 *
 * Rendered in PastLivesFlow when state = 'free-reveal' (freemium flag on).
 * The user sees the hero life snippet before being offered the two paid upgrade
 * tiers. A tertiary link below the CTAs lets them navigate to the natal chart.
 */

import { useNavigate } from 'react-router-dom';
import { useHeroPastLife } from '../../hooks/useHeroPastLife';
import { ReportMarkdown } from '../ReportMarkdown';
import { MeSubpageShell } from '../chrome/MeSubpageShell';
import { useTranslation } from 'react-i18next';

interface FreeRevealStepProps {
  /** Called when user taps "Узнать все 12 жизней текстом" (99⭐ text tier). */
  onTextTier: () => void;
  /** Called when user taps "С портретами" (299⭐ portrait tier). */
  onPortraitTier: () => void;
  onBack: () => void;
}

export function FreeRevealStep({
  onTextTier,
  onPortraitTier,
  onBack,
}: FreeRevealStepProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Hero past-life: enabled as soon as chart is ready.
  const { markdown: heroMarkdown, ready: heroReady, isLoading: heroLoading } = useHeroPastLife(true);

  const isLoading = heroLoading;

  return (
    <MeSubpageShell
      title={t('pastLives.freeReveal.title', { defaultValue: 'Твой след в вечности' })}
      onBack={onBack}
      data-testid="past-lives-free-reveal"
    >
      {isLoading && (
        <p data-testid="past-lives-free-reveal-loading" style={{ textAlign: 'center', opacity: 0.7 }}>
          {t('common:states.loading')}
        </p>
      )}

      {!isLoading && (
        <>
          {/* ── Hero life teaser section ── */}
          {heroReady && heroMarkdown && (
            <div data-testid="past-lives-hero-section" style={{ marginBottom: 32 }}>
              <h2 style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--cream, #f5e9c7)',
                marginBottom: 8,
              }}>
                {t('pastLives.freeReveal.heroSectionTitle', { defaultValue: 'Твоя ключевая прошлая жизнь' })}
              </h2>
              <ReportMarkdown markdown={heroMarkdown} />
            </div>
          )}

          {/* ── CTA buttons ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
            <button
              type="button"
              data-testid="past-lives-text-tier-btn"
              style={{
                width: '100%',
                padding: '13px 16px',
                background: 'linear-gradient(135deg, rgba(180,140,90,0.25), rgba(140,100,60,0.35))',
                border: '1px solid rgba(200,165,100,0.4)',
                borderRadius: 12,
                color: 'var(--cream, #f5e9c7)',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
              onClick={onTextTier}
            >
              {t('pastLives.freeReveal.textTierCta', { defaultValue: 'Узнать все 12 жизней — 99 ⭐' })}
            </button>

            <button
              type="button"
              data-testid="past-lives-portrait-tier-btn"
              style={{
                width: '100%',
                padding: '13px 16px',
                background: 'linear-gradient(135deg, rgba(100,80,180,0.2), rgba(80,60,150,0.3))',
                border: '1px solid rgba(160,140,220,0.4)',
                borderRadius: 12,
                color: 'var(--cream, #f5e9c7)',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
              onClick={onPortraitTier}
            >
              {t('pastLives.freeReveal.portraitTierCta', { defaultValue: 'С портретами — 299 ⭐' })}
            </button>

            <button
              type="button"
              data-testid="past-lives-goto-natal"
              style={{
                width: '100%',
                padding: '11px 16px',
                background: 'transparent',
                border: '1px solid rgba(200,165,100,0.25)',
                borderRadius: 12,
                color: 'rgba(245,233,199,0.65)',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
              onClick={() => navigate('/me/natal-chart?autoLoad=1')}
            >
              {t('pastLives.freeReveal.gotoNatalCta', { defaultValue: 'перейти почитать натальную карту' })}
            </button>
          </div>
        </>
      )}
    </MeSubpageShell>
  );
}
